import { AxiosResponse } from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { useFileUpload } from "@/hooks/storage";
import { CreateSectionRequest, UpdateSectionRequest } from "@/types/requests";
import { Cell, DraftCell } from "@/types/resources";
import {
  CreateStudySectionResponse,
  DeleteStudySectionResponse,
  GetStudySectionsResponse,
  UpdateStudySectionResponse,
} from "@/types/responses";

const isAsset = (value: unknown): value is ImagePickerAsset =>
  typeof value === "object" && value !== null && "uri" in value;

/**
 * Uploads any freshly-picked assets embedded in cells and returns wire-ready
 * `Cell`s (values reduced to S3 keys / text, `<name>_url` presigned siblings
 * dropped). Mirrors the old `media[]` upload flow, but the images now live
 * inside the cell values keyed by their image variable.
 */
const useCellUploader = (studyId: string) => {
  const { uploadFile } = useFileUpload();
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadCells = useCallback(
    async (cells: DraftCell[]): Promise<Cell[]> => {
      /* Flatten to the asset values first so we can report a single
         aggregate progress across every image in every cell. */
      const assetCount = cells.reduce(
        (count, cell) =>
          count + Object.values(cell).filter((v) => isAsset(v)).length,
        0
      );
      const progresses: number[] = new Array(assetCount).fill(0);
      let assetIndex = 0;

      const uploaded = await Promise.all(
        cells.map(async (cell) => {
          const entries = await Promise.all(
            Object.entries(cell)
              /* Drop presigned-URL siblings — they're read-only echoes and
                 the backend strips them anyway. */
              .filter(([key]) => !key.endsWith("_url"))
              .map(async ([key, value]): Promise<[string, string | null]> => {
                if (!isAsset(value)) return [key, value ?? null];
                const currentIndex = assetIndex++;
                const path = await uploadFile({
                  file: value,
                  folder: "studies/sections",
                  resourceId: studyId,
                  onProgress: (progress) => {
                    progresses[currentIndex] = progress;
                    setUploadProgress(
                      assetCount > 0
                        ? progresses.reduce((sum, p) => sum + p, 0) / assetCount
                        : 0
                    );
                  },
                });
                return [key, path];
              })
          );
          return Object.fromEntries(entries) as Cell;
        })
      );

      return uploaded;
    },
    [studyId, uploadFile]
  );

  return { setUploadProgress, uploadCells, uploadProgress };
};

const useGetSections = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const client = useAxiosClient(`/studies/${studyId}`);

  const fetchSections = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await client.get<GetStudySectionsResponse>("/sections");
      return response.data.sections;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    error,
    fetchSections,
    loading,
  };
};

const useCreateSection = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(`/studies/${studyId}`);
  const { setUploadProgress, uploadCells, uploadProgress } =
    useCellUploader(studyId);

  const createSection = useCallback(
    async (
      data: Omit<CreateSectionRequest, "cells"> & { cells?: DraftCell[] }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const cells =
          data.cells !== undefined ? await uploadCells(data.cells) : undefined;
        const response = await client.post<
          CreateStudySectionResponse,
          AxiosResponse<CreateStudySectionResponse>,
          CreateSectionRequest
        >("/sections", {
          ...data,
          cells,
          title: data.title?.trim(),
        });
        /* Return both pieces — the section list always grows by one, but
           use-case sections also seed a question battery the caller needs
           to splice into its local list. */
        return {
          questions: response.data.questions,
          section: response.data.section,
        };
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, setUploadProgress, uploadCells]
  );

  return {
    createSection,
    error,
    loading,
    uploadProgress,
  };
};

/**
 * PATCH-style update. Only fields you explicitly pass are persisted; omit a
 * field to leave it untouched. `cells` may embed freshly-picked
 * `ImagePickerAsset`s in image variables — they're uploaded inline and reduced
 * to S3 keys before the request. Whenever `cells` is sent you must also send
 * `config` (the backend validates cells against `config.variables`).
 */
const useUpdateSection = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(`/studies/${studyId}/sections`);
  const { setUploadProgress, uploadCells, uploadProgress } =
    useCellUploader(studyId);

  const updateSection = useCallback(
    async (
      sectionId: string,
      data: Omit<UpdateSectionRequest, "cells"> & { cells?: DraftCell[] }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const payload: UpdateSectionRequest = {};
        if (data.title !== undefined) payload.title = data.title.trim();
        if (data.description !== undefined) {
          payload.description = data.description?.trim() || null;
        }
        if (data.config !== undefined) payload.config = data.config;
        if (data.cells !== undefined)
          payload.cells = await uploadCells(data.cells);

        const response = await client.put<
          UpdateStudySectionResponse,
          AxiosResponse<UpdateStudySectionResponse>,
          UpdateSectionRequest
        >(`/${sectionId}`, payload);
        /* Symmetric with `useCreateSection.createSection`: the caller gets
           back both the updated section and its current questions in one
           shape, so editor / picker flows can reconcile state without a
           second roundtrip. */
        return {
          questions: response.data.questions,
          section: response.data.section,
        };
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, setUploadProgress, uploadCells]
  );

  return {
    error,
    loading,
    updateSection,
    uploadProgress,
  };
};

const useDeleteSection = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient(`/studies/${studyId}/sections`);

  const deleteSection = useCallback(
    async (sectionId: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        await client.delete<DeleteStudySectionResponse>(`/${sectionId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    deleteSection,
    error,
    loading,
  };
};

export { useCreateSection, useDeleteSection, useGetSections, useUpdateSection };
